// @ts-nocheck
import TextField from "@store/MobiusOverrides/TextField/TextField";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    dataKey = "dataKey",
    validator = "validator",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.dataKey]: string;
        [fields.validator]: string;
    };
}

type Props = OwnProps;

const ContactUsTextField: React.FC<Props> = ({ fields }) => {
    const { label, hintText, isRequired, dataKey, validator } = fields;
    const dispatch = useDispatch();
    const contactFormContext = React.useContext(ContactFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    const value = useSelector((state: ApplicationState) => state.components.contactUsForm.fieldValues[dataKey]);
    const handleValidation = (newValue?: string) => {
        const valueToValidate = newValue ?? value;
        const errorText = siteMessage(`ContactUs_Error_${dataKey}`) as string;
        if (!validator) {
            const errorMessage = isRequired && !valueToValidate ? errorText : "";
            setErrorMessage(errorMessage);
            return !errorMessage;
        }
        const isValidRegex = new RegExp(validator);
        const errorMessage = isRequired && !isValidRegex.test(value ?? "") ? errorText : "";
        setErrorMessage(errorMessage);
        return !errorMessage;
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        const newValue = event.currentTarget.value;
        dispatch(setFieldValue({ key: dataKey, value: newValue }));
        handleValidation(newValue);
        contactFormContext.validators[dataKey] = () => handleValidation(newValue);
    };
    useEffect(() => {
        contactFormContext.validators[dataKey] = handleValidation;
    }, [isRequired]);

    if (!contactFormContext) {
        return null;
    }

    return (
        <TextField
            label={label}
            value={value ?? ""}
            onChange={handleChange}
            error={errorMessage}
            required={isRequired}
            placeholder={hintText}
        />
    );
};

const handleRegexValidation = (value: string) => {
    try {
        // only way to actually test if a regex is valid is to try to create a new RegExp object
        // so we need to tell eslint that its okay to use as a side effect
        // eslint-disable-next-line no-new
        new RegExp(value);
        return null;
    } catch {
        return "Invalid regular expression";
    }
};

const widgetModule: WidgetModule = {
    component: ContactUsTextField,
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },

            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.isRequired,
                displayName: "Is Required",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.dataKey,
                displayName: "Data Key",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.validator,
                displayName: "Validator",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                instructions: "Provide a regular expression to validate the field",
                validate: handleRegexValidation,
            },
        ],
    },
};

export default widgetModule;
